/*
 * <cart-upsell> — automatic cross-sell for the Opus cart drawer.
 *
 * On connect it seeds the Shopify Product Recommendations API with the first
 * line item's product id and injects the returned cards. Because Dawn re-renders
 * the whole `.drawer__inner` server-side on every cart change, this element is
 * recreated each time the cart updates, so connectedCallback re-runs with a fresh
 * seed automatically.
 *
 * This file also carries the delegated "Clear cart" handler (bottom).
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.results = this.querySelector('.opus-cart-upsell__results');
    this.productId = this.dataset.productId;

    // No product to seed from (empty cart) — stay hidden.
    if (!this.productId) {
      this.hidden = true;
      return;
    }

    this.loadRecommendations();
    this.addEventListener('submit', this.onAddSubmit.bind(this));
    // Delegated so these keep working after results are re-rendered.
    this.addEventListener('change', this.onVariantChange.bind(this));
    this.addEventListener('click', this.onNavClick.bind(this));

    // On the cart PAGE only `.js-contents` re-renders on quantity changes, so this
    // element survives and must reseed itself.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onCartUpdate(event) {
    const firstItem = event && event.cartData && event.cartData.items && event.cartData.items[0];
    if (firstItem) {
      this.reseed(firstItem.product_id);
    } else {
      fetch(`${window.routes ? window.routes.cart_url : '/cart'}.js`)
        .then((r) => r.json())
        .then((cart) => this.reseed(cart.items[0] && cart.items[0].product_id));
    }
  }

  reseed(productId) {
    if (!productId) {
      this.hidden = true;
      return;
    }
    if (String(productId) === String(this.productId)) return;
    this.productId = String(productId);
    this.loadRecommendations();
  }

  loadRecommendations() {
    const url = `${this.dataset.url}&product_id=${this.productId}`;

    // Dawn re-renders the drawer on every cart change, which recreates this
    // element and would otherwise refetch the same recommendations each time.
    const cached = CartUpsell.cache.get(url);
    if (cached !== undefined) {
      this.render(cached);
      return;
    }

    const inflight =
      CartUpsell.pending.get(url) ||
      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const results = html.querySelector('.opus-cart-upsell__results');
          const markup = results && results.innerHTML.trim().length ? results.innerHTML : '';
          CartUpsell.cache.set(url, markup);
          CartUpsell.pending.delete(url);
          return markup;
        });

    CartUpsell.pending.set(url, inflight);

    inflight
      .then((markup) => this.render(markup))
      .catch((e) => {
        CartUpsell.pending.delete(url);
        console.error('Cart upsell failed to load', e);
        this.hidden = true;
      });
  }

  render(markup) {
    if (markup) {
      this.results.innerHTML = markup;
      this.hidden = false;
    } else {
      this.hidden = true;
    }
  }

  // Chevron paging. Scrolls by one card plus its gap so cards land flush.
  onNavClick(event) {
    const nav = event.target.closest('[data-upsell-scroll]');
    if (!nav) return;

    const list = this.querySelector('.opus-cart-upsell__list');
    if (!list) return;

    const card = list.querySelector('.opus-cart-upsell__card');
    const gap = parseFloat(getComputedStyle(list).columnGap || '0') || 0;
    const step = card ? card.offsetWidth + gap : list.clientWidth;

    list.scrollBy({ left: Number(nav.dataset.upsellScroll) * step, behavior: 'smooth' });
  }

  // Keep price AND posted quantity in step with the chosen variant.
  onVariantChange(event) {
    const select = event.target.closest('[data-upsell-variants]');
    if (!select) return;

    // CRITICAL: <cart-upsell> renders inside <cart-drawer-items>, and Dawn's
    // CartItems base class binds a `change` listener that treats ANY change
    // inside it as a line-item quantity edit (onChange -> validateQuantity).
    // Our variant <select> has no data-min/step, so that validation produced
    // "You can only add this item in increments of undefined". Keep the event
    // inside this component.
    event.stopPropagation();

    const card = select.closest('.opus-cart-upsell__card');
    const option = select.selectedOptions[0];
    if (!card || !option) return;

    const priceEl = card.querySelector('[data-upsell-price]');
    if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price;

    // Quantity rules are per-variant: one variant may sell singly while another
    // must go in multiples. Post the selected variant's minimum, rounded up to
    // its increment, otherwise Shopify rejects the add.
    const qtyInput = card.querySelector('[data-upsell-quantity]');
    if (!qtyInput) return;

    const min = parseInt(option.dataset.qtyMin, 10) || 1;
    const increment = parseInt(option.dataset.qtyIncrement, 10) || 1;
    qtyInput.value = increment > 1 ? Math.ceil(min / increment) * increment : min;
  }

  onAddSubmit(event) {
    const form = event.target.closest('.opus-cart-upsell__form');
    if (!form) return;

    event.preventDefault();
    // Same reasoning as onVariantChange — don't let this reach cart-drawer-items.
    event.stopPropagation();

    const button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-disabled', 'true');

    const cartDrawer = document.querySelector('cart-drawer');
    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer,cart-icon-bubble');
    formData.append('sections_url', window.location.pathname);

    fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status) {
          // Add rejected. Show Shopify's own description rather than failing silently.
          console.error('Cart upsell add error', parsed);
          if (button) {
            const original = button.textContent;
            button.textContent = parsed.description || parsed.message || 'Unavailable';
            setTimeout(() => {
              button.textContent = original;
            }, 2500);
          }
          return;
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsed.sections) {
          cartDrawer.renderContents(parsed);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', productVariantId: formData.get('id') });
        }
      })
      .catch((e) => console.error('Cart upsell add failed', e))
      .finally(() => {
        if (button) button.removeAttribute('aria-disabled');
      });
  }
}

// Page-lifetime caches shared by every <cart-upsell> instance.
CartUpsell.cache = new Map();
CartUpsell.pending = new Map();

customElements.define('cart-upsell', CartUpsell);

/*
 * Clear cart. Delegated from the document so it survives the drawer being
 * re-rendered. Zeroes every line in ONE /cart/update.js call rather than
 * looping /cart/change.js per item.
 */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-clear]');
  if (!button || button.hasAttribute('aria-busy')) return;

  const scope = button.closest('cart-drawer') || document;
  const keys = Array.from(scope.querySelectorAll('[data-quantity-line-key]'))
    .map((input) => input.dataset.quantityLineKey)
    .filter(Boolean);

  if (!keys.length) return;

  const updates = {};
  keys.forEach((key) => {
    updates[key] = 0;
  });

  const cartDrawer = document.querySelector('cart-drawer');
  const sections = cartDrawer ? ['cart-drawer', 'cart-icon-bubble'] : ['main-cart-items', 'cart-icon-bubble'];

  button.setAttribute('aria-busy', 'true');

  fetch(`${window.routes ? window.routes.cart_update_url : '/cart/update'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
  })
    .then((response) => response.json())
    .then((parsed) => {
      if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsed.sections) {
        // Dawn's renderContents never sets the empty state (only its quantity code does), and
        // the empty message/layout only show on cart-drawer.is-empty, so set it here.
        cartDrawer.classList.toggle('is-empty', parsed.item_count === 0);
        cartDrawer.renderContents(parsed);
      } else {
        window.location.reload();
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-clear', cartData: parsed });
      }
    })
    .catch((e) => {
      console.error('Clear cart failed', e);
      window.location.reload();
    })
    .finally(() => button.removeAttribute('aria-busy'));
});